import { Button, ButtonLevel, ButtonSize } from '@vizit/atoms';
import { XCloseIcon } from '@vizit/icons';
import { CirclesDecoration } from '@vizit/illustrations';
import { colors, cva, cx } from '@vizit/theme';
import { Dialog, type DialogProps, Modal, composeRenderProps } from 'react-aria-components';

interface DialogContentProps extends Omit<React.ComponentPropsWithoutRef<typeof Modal>, 'children'> {
  /**
   * Accessible label for the dialog when no Heading with slot="title" is used.
   * @optional
   */
  'aria-label'?: string;
  /**
   * ID of element that labels the dialog when no Heading with slot="title" is used.
   * @optional
   */
  'aria-labelledby'?: string;
  /**
   * Whether the dialog allows elements to overflow the modal container
   *
   * @optional
   * @default false
   */
  allowOverflow?: boolean;
  /**
   * Whether the close button is disabled
   *
   * @optional
   * @default false
   */
  closeButtonIsDisabled?: boolean;
  /**
   * The content of the dialog.
   *
   * @optional
   */
  children?: DialogProps['children'];
  /**
   * The class name of the dialog.
   *
   * @optional
   */
  dialogClassName?: string;
  /**
   * Whether to show the circles decoration
   *
   * @optional
   * @boolean
   * @default false
   */
  showDecoration?: boolean;
}

const modalClass = cva({
  base: 'fixed left-[50%] top-[50%] z-50 w-full max-w-xl translate-x-[-50%] translate-y-[-50%] bg-primary duration-200 data-[exiting]:duration-300 data-[entering]:animate-in data-[exiting]:animate-out data-[entering]:fade-in-0 data-[exiting]:fade-out-0 data-[entering]:zoom-in-95 data-[exiting]:zoom-out-95 data-[entering]:slide-in-from-left-1/2 data-[entering]:slide-in-from-top-[48%] data-[exiting]:slide-out-to-left-1/2 data-[exiting]:slide-out-to-top-[48%] relative max-h-[calc(100dvh-32px)] flex flex-col',
  variants: {
    allowOverflow: {
      true: 'overflow-visible',
      false: 'overflow-hidden',
    },
  },
});

function DialogContent(props: DialogContentProps) {
  const {
    allowOverflow = false,
    closeButtonIsDisabled = false,
    showDecoration = false,
    children: childrenProp,
    className,
    dialogClassName,
    isDismissable = true,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...other
  } = props;

  return (
    <Modal className={cx(modalClass({ allowOverflow }), className)} isDismissable={isDismissable} {...other}>
      {showDecoration && (
        <CirclesDecoration size={336} className="absolute -top-60 -left-60" color={colors['gray-utility']['200']} />
      )}
      <Dialog
        className={cx(
          'flex flex-col flex-1 min-h-0 gap-16 bg-transparent relative z-10 overflow-hidden',
          dialogClassName,
        )}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        {composeRenderProps(childrenProp, (children, { close, ...rest }) => (
          <>
            {typeof children === 'function'
              ? (children as (props: { close: () => void }) => React.ReactNode)({ close, ...rest })
              : children}

            {isDismissable && (
              <Button
                aria-label="close"
                isDisabled={closeButtonIsDisabled}
                size={ButtonSize.Sm}
                level={ButtonLevel.Ghost}
                className="absolute right-12 top-12 z-30"
                onPress={close}
                iconStart={<XCloseIcon size={20} color={colors['gray-utility']['400']} />}
              />
            )}
          </>
        ))}
      </Dialog>
    </Modal>
  );
}

export { DialogContent };
